/* eslint-disable no-undef */
import React, { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  CloseIcon,
  JoinAudio,
  Knock,
  LeaveAudio,
  PinIcon
} from 'assets/images';
import { Colors } from 'util/constants';
import {
  sendMultiKnockMessage,
  sendRemoteAudioCommand
} from 'store/modules/chat';
import { fetchUserInfo } from 'store/modules/login/actions';
import withBoundary from 'component/ErrorBoundary/withBoundary';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import withStyles from '@mui/styles/withStyles';
import PropTypes from 'prop-types';
import DialogPortal from 'modules/Conference/components/Popover/DialogPortal';
const Popper = (props) => {
  const { classes, participant, anchorEl, pinned, isRemoteParticipant } = props;
  const _profiles = useSelector((state) => state['features/login'].profiles);
  const _remoteParticipants = useSelector(
    (state) => state['features/filmstrip'].remoteParticipants
  );
  const customUserid = participant?.customUserid;
  const participantProfile = _profiles?.[customUserid];
  const active = useSelector(
    (state) => state['features/filmstrip'].thumbnailPopup
  );
  const dispatch = useDispatch();
  const timeoutRef = useRef(undefined);

  const [anchorSize, setAnchorSize] = useState(null);
  const [thumbnailCutout, setThumbnailCutout] = useState(null);
  const [portalStyle, setPortalStyle] = useState(null);
  const lastAnchorBoundsRef = useRef(null);

  useLayoutEffect(() => {
    if (!anchorEl) return;

    const bounds = anchorEl.getBoundingClientRect();
    const previousBounds = lastAnchorBoundsRef.current;

    const changed =
      !previousBounds ||
      bounds.x !== previousBounds.x ||
      bounds.y !== previousBounds.y ||
      bounds.width !== previousBounds.width ||
      bounds.height !== previousBounds.height;

    if (changed) {
      setPortalStyle({
        left: `${bounds.left - 10}px`,
        bottom: `${window.innerHeight - bounds.bottom - 10}px`
      });
      setAnchorSize({
        width: bounds.width,
        height: bounds.height
      });
    }

    const roundedThumbnail = anchorEl.querySelector('.thumbnail-container');
    const roundedBounds = roundedThumbnail?.getBoundingClientRect();

    if (roundedBounds?.width > 0 && roundedBounds?.height > 0) {
      const roundedStyle = window.getComputedStyle(roundedThumbnail);
      // Leave room for the name/status strip shown below the thumbnail on remotes.
      const fillBottom = roundedBounds.bottom - (isRemoteParticipant ? 13 : 0);

      setThumbnailCutout({
        style: {
          // The surround's inner edge is 2px inside the anchor.
          left: roundedBounds.left - bounds.left - 2,
          top: roundedBounds.top - bounds.top - 2,
          width: roundedBounds.width,
          height: roundedBounds.height,
          borderRadius: roundedStyle.borderRadius
        },
        fillHeight: Math.max(0, fillBottom - bounds.top - 2)
      });
    } else {
      setThumbnailCutout(null);
    }

    lastAnchorBoundsRef.current = bounds;
  }, [anchorEl, _remoteParticipants, isRemoteParticipant]);

  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      _onClose();
    }, 60 * 1000);
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = undefined;
      }
    };
  }, []);

  useEffect(() => {
    if (active && active !== participant.id) {
      _onClose();
    }
  }, [active]);

  useEffect(() => {
    if (customUserid && customUserid !== 'off' && !participantProfile) {
      dispatch(fetchUserInfo(customUserid));
    }
  }, [dispatch, customUserid, participantProfile]);

  const option = {
    backgroundColor: 'rgba(255,255,255, 0.8)',
    padding: '10px !important',
    fontStyle: 'normal',
    fontWeight: '500',
    minWidth: '40px',
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'center'
  };

  const onKnock = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(sendMultiKnockMessage(participant?.id));
  };

  const onJoinAudio = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(sendRemoteAudioCommand(participant?.id, 'join'));
  };

  const onLeaveAudio = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(sendRemoteAudioCommand(participant?.id, 'leave'));
  };

  const _onClose = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
    props.onClose();
  };

  useEffect(() => {
    // TODO: Add popover movement/resizing in response to scroll/window resize events.
    // For now they just trigger a close
    const scrollContainer = anchorEl?.closest('.filmstrip__videos-scrollable');
    const handleClose = () => {
      _onClose();
    };
    scrollContainer?.addEventListener('scroll', handleClose);
    window.addEventListener('resize', handleClose);

    return () => {
      scrollContainer?.removeEventListener('scroll', handleClose);
      window.removeEventListener('resize', handleClose);
    };
  }, [anchorEl, _onClose]);

  return (
    <DialogPortal className={classes.popperContainer} style={portalStyle}>
      <Box
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onTouchStart={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            marginBottom: '12px'
          }}>
          <Box
            className={classes.buttonBackground}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              _onClose();
            }}>
            <CloseIcon
              style={{ width: '20px', display: 'flex', padding: '11px' }}
            />
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
            borderRadius: '12px',
            minWidth: anchorSize ? anchorSize.width + 50 : undefined
          }}>
          {!participant?.local &&
          !participant?.fakeParticipant &&
          participant?.customIsAudioAvailable === 'on' ? (
            <Box
              onClick={onLeaveAudio}
              sx={{
                ...option,
                flex: 1.3,
                borderRadius: '12px 0px 0px 0px'
              }}>
              <Box className={classes.img}>
                <LeaveAudio
                  fill={Colors.red}
                  style={{ width: '1rem', fill: Colors.red }}
                />
              </Box>
              <Typography variant="labelTiny" className={classes.text}>
                leave audio
              </Typography>
            </Box>
          ) : (
            <Box
              onClick={onJoinAudio}
              sx={{
                ...option,
                flex: 1.3,
                borderRadius: '12px 0px 0px 0px'
              }}>
              <Box className={classes.img}>
                <JoinAudio
                  fill={Colors.green}
                  style={{ width: '1rem', fill: Colors.green }}
                />
              </Box>
              <Typography variant="labelTiny" className={classes.text}>
                join audio
              </Typography>
            </Box>
          )}
          <Box
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              props.onPin(!pinned);
            }}
            sx={{
              ...option,
              borderRadius: '0px',
              boxShadow: 'none !important'
            }}>
            <Box className={classes.img}>
              {pinned ? (
                <PinIcon style={{ width: '1rem', color: Colors.gray }} />
              ) : (
                <PinIcon style={{ width: '1rem', color: Colors.blue }} />
              )}
            </Box>
            <Typography variant="labelTiny" className={classes.text}>
              {pinned ? 'unpin' : 'pin'}
            </Typography>
          </Box>
          <Box
            onClick={onKnock}
            sx={{
              ...option,
              borderRadius: '0px 12px 12px 0px'
            }}>
            <Box className={classes.img}>
              <Knock style={{ width: '1rem', fill: Colors.orange }} />
            </Box>
            <Typography variant="labelTiny" className={classes.text}>
              knock
            </Typography>
          </Box>
        </Box>
        <Box
          className={classes.transparentView}
          sx={{
            width: anchorSize ? anchorSize.width + 20 : undefined,
            height: anchorSize ? anchorSize.height + 20 : undefined
          }}>
          {thumbnailCutout && (
            <Box
              aria-hidden="true"
              className={classes.thumbnailCornerFill}
              sx={{ height: thumbnailCutout.fillHeight }}>
              <Box
                className={classes.thumbnailCutout}
                sx={thumbnailCutout.style}
              />
            </Box>
          )}
        </Box>
      </Box>
    </DialogPortal>
  );
};

const styles = (theme) => {
  return {
    transparentView: {
      position: 'relative',
      zIndex: 99,
      backgroundColor: 'transparent',
      boxSizing: 'border-box',
      boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
      border: '12px solid rgba(255, 255, 255, 0.8)',
      borderRadius: '0px 0px 12px 12px',
      padding: 0
    },
    thumbnailCornerFill: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      maxHeight: '100%',
      overflow: 'hidden',
      pointerEvents: 'none'
    },
    thumbnailCutout: {
      position: 'absolute',
      boxShadow: '0 0 0 100vmax rgba(255, 255, 255, 0.8)'
    },
    popperContainer: {
      position: 'fixed',
      zIndex: '9999 !important',
      backgroundColor: 'transparent !important'
    },
    img: {
      justifyContent: 'center',
      display: 'flex'
    },
    text: {
      ...theme.typography.labelTiny,
      textAlign: 'center',
      color: Colors.black,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    buttonBackground: {
      background: '#FFFFFF',
      boxShadow: '0px 4px 8px rgb(0 0 0 / 15%)',
      borderRadius: '100px'
    }
  };
};

export default React.memo(withStyles(styles)(withBoundary(Popper)));
Popper.propTypes = {
  active: PropTypes.bool,
  classes: PropTypes.object,
  localParticipant: PropTypes.any,
  isRemoteParticipant: PropTypes.bool,
  anchorEl: PropTypes.instanceOf(HTMLElement).isRequired,
  onClose: PropTypes.func,
  onPin: PropTypes.func,
  participant: PropTypes.any,
  pinned: PropTypes.bool
};
